import React, { useCallback, useEffect, useRef, useState } from 'react';

interface Column<T> {
  key: string;
  header: React.ReactNode;
  render?: (item: T) => React.ReactNode;
  className?: string;
  /** Pins the record identity or action column while a wide table scrolls. */
  sticky?: 'primary' | 'actions';
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (item: T) => void;
  expandedRow?: (item: T) => React.ReactNode | null;
  emptyMessage?: string;
}

// Helper — does this column carry a meaningful label we can show on a mobile card?
// Empty / icon-only / select / action columns are rendered without a label.
const labelFor = (header: React.ReactNode, key: string): string | null => {
  if (key === '_select' || key === '_actions' || key === 'actions') return null;
  if (typeof header === 'string') {
    const t = header.trim();
    if (!t) return null;
    return t;
  }
  return null;
};

export type TableColumnRole = 'primary' | 'actions' | null;

/** Explicit metadata wins; legacy tables receive safe identity/action defaults. */
export const columnRoleFor = <T,>(columns: Column<T>[], index: number): TableColumnRole => {
  const column = columns[index];
  if (column.sticky) return column.sticky;
  if (column.key === '_actions' || column.key === 'actions') return 'actions';
  const firstLabeled = columns.findIndex((candidate) => Boolean(labelFor(candidate.header, candidate.key)));
  return index === firstLabeled ? 'primary' : null;
};

const stickyClassFor = (role: TableColumnRole): string =>
  role ? `intrlock-table-sticky intrlock-table-sticky-${role}` : '';

export function Table<T extends { id: number | string; uuid?: string }>({ columns, data, onRowClick, expandedRow, emptyMessage = 'No data found' }: TableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [hasMoreRight, setHasMoreRight] = useState(false);

  const measureOverflow = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    const overflowing = node.scrollWidth - node.clientWidth > 2;
    setHasHorizontalOverflow(overflowing);
    setHasMoreRight(overflowing && node.scrollLeft + node.clientWidth < node.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    measureOverflow();
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(node);
    const table = node.querySelector('table');
    if (table) observer.observe(table);
    node.addEventListener('scroll', measureOverflow, { passive: true });
    window.addEventListener('resize', measureOverflow);
    return () => {
      observer.disconnect();
      node.removeEventListener('scroll', measureOverflow);
      window.removeEventListener('resize', measureOverflow);
    };
  }, [measureOverflow, columns.length, data.length]);

  return (
    <>
      {/* ── Desktop table (md+) ─────────────────────────────────────────── */}
      <div className="intrlock-table-region hidden md:block">
        {hasHorizontalOverflow && hasMoreRight && (
          <div className="intrlock-table-overflow-hint" role="status" aria-live="polite">
            More columns are available. Scroll right <span aria-hidden="true">→</span>
          </div>
        )}
        <div ref={scrollRef} className="intrlock-table-wrap overflow-x-auto">
        <table className="intrlock-table w-full">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((col, colIndex) => (
                <th key={col.key} data-column-role={columnRoleFor(columns, colIndex) || undefined} className={`text-center whitespace-nowrap text-sm font-bold text-gray-700 uppercase tracking-wider py-4 px-4 ${stickyClassFor(columnRoleFor(columns, colIndex))} ${col.className || ''}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-12 text-gray-400">{emptyMessage}</td></tr>
            ) : (
              data.map((item) => {
                const expandedContent = expandedRow ? expandedRow(item) : null;
                return (
                  <React.Fragment key={item.id}>
                    <tr onClick={() => onRowClick?.(item)} className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors`}>
                      {columns.map((col, colIndex) => (
                        <td key={col.key} data-column-role={columnRoleFor(columns, colIndex) || undefined} className={`py-3 px-4 text-center text-sm text-gray-700 ${stickyClassFor(columnRoleFor(columns, colIndex))} ${col.className || ''}`}>
                          {col.render ? col.render(item) : (item as any)[col.key]}
                        </td>
                      ))}
                    </tr>
                    {expandedContent && (
                      <tr>
                        <td colSpan={columns.length} className="p-0">
                          {expandedContent}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* ── Mobile cards (<md) ──────────────────────────────────────────────
          Auto-generated from the same columns: first labeled column becomes
          the card title, the rest render as label / value rows. Columns
          without a label (selection checkbox, action buttons) collapse into
          a small footer row so they remain clickable.                       */}
      <div className="md:hidden p-3 space-y-2">
        {data.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">
            {emptyMessage}
          </div>
        ) : (
          data.map((item) => {
            const expandedContent = expandedRow ? expandedRow(item) : null;

            // The explicit/derived primary column becomes the card title too.
            let titleColIndex = columns.findIndex((_, index) => columnRoleFor(columns, index) === 'primary');
            if (titleColIndex < 0) titleColIndex = columns.findIndex((column) => Boolean(labelFor(column.header, column.key)));
            const titleCol = titleColIndex >= 0 ? columns[titleColIndex] : null;

            return (
              <React.Fragment key={item.id}>
                <div
                  className={`bg-white rounded-xl border border-gray-200 p-3 ${onRowClick ? 'cursor-pointer active:bg-gray-50' : ''}`}
                  onClick={() => onRowClick?.(item)}
                >
                  {/* Title row */}
                  {titleCol && (
                    <div className="text-base font-semibold text-gray-900 mb-1.5">
                      {titleCol.render ? titleCol.render(item) : (item as any)[titleCol.key]}
                    </div>
                  )}

                  {/* Detail rows */}
                  <div className="space-y-1 text-xs">
                    {columns.map((col, idx) => {
                      if (idx === titleColIndex) return null;
                      const lbl = labelFor(col.header, col.key);
                      if (!lbl) return null;
                      const val = col.render ? col.render(item) : (item as any)[col.key];
                      return (
                        <div key={col.key} className="flex items-baseline gap-2">
                          <span className="text-gray-400 uppercase tracking-wider text-[10px] font-medium min-w-[80px] flex-shrink-0">{lbl}</span>
                          <span className="text-gray-700 min-w-0 flex-1 break-words">{val}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer row for actions / unlabeled columns */}
                  {(() => {
                    const footerCols = columns.filter((c, idx) => idx !== titleColIndex && (columnRoleFor(columns, idx) === 'actions' || !labelFor(c.header, c.key)));
                    if (footerCols.length === 0) return null;
                    return (
                      <div
                        className="flex items-center gap-3 pt-2 mt-2 border-t border-gray-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {footerCols.map((col) => (
                          <span key={col.key}>
                            {col.render ? col.render(item) : (item as any)[col.key]}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Expanded panel — same content as the desktop expanded row */}
                {expandedContent && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {expandedContent}
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>
    </>
  );
}
